import {
  Component,
  type PropsWithChildren,
  type ReactNode,
  useState,
} from 'react';
import type { ErrorDisplayProps } from '@backstage/frontend-plugin-api';
import { TdsButton } from '@scania/tegel-react';
import { appRoutePaths } from '../Root/navigation';
import { TegelButtonBase } from './Tegel';

const supportEmail = 'devtools@scania.com';

export type DrivelineErrorAction = {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'ghost';
};

export type DrivelineErrorPageProps = {
  details?: ReactNode;
  eyebrow?: string;
  message: ReactNode;
  primaryAction?: DrivelineErrorAction;
  secondaryActions?: DrivelineErrorAction[];
  status?: string;
  title: string;
};

const assignLocation = (path: string) => {
  window.location.assign(path);
};

const reloadPage = () => {
  window.location.reload();
};

const goHome = () => {
  assignLocation(appRoutePaths.home);
};

const goBack = () => {
  if (window.history.length > 1) {
    window.history.back();
    return;
  }

  goHome();
};

const errorDetails = (error?: Error) =>
  [error?.name, error?.message, error?.stack].filter(Boolean).join('\n\n');

const ActionButton = ({ action }: { action: DrivelineErrorAction }) => (
  <TdsButton
    text={action.label}
    size="md"
    variant={action.variant ?? 'secondary'}
    onClick={action.onClick}
  />
);

const DrivelineErrorIllustration = () => (
  <svg
    className="driveline-error-illustration"
    data-testid="driveline-error-illustration"
    viewBox="0 0 520 360"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    focusable="false"
  >
    <rect
      className="driveline-error-illustration-panel"
      x="44"
      y="42"
      width="430"
      height="250"
      rx="4"
    />
    <path
      className="driveline-error-illustration-route"
      d="M82 244C122 210 159 226 190 246C225 269 262 270 295 238C330 205 365 205 405 232"
    />
    <path className="driveline-error-illustration-road" d="M58 292H462" />
    <path
      className="driveline-error-illustration-road driveline-error-illustration-road-muted"
      d="M112 318H408"
    />
    <g className="driveline-error-illustration-truck">
      <path d="M118 192H292V254H118V192Z" />
      <path d="M292 212H348L386 254H292V212Z" />
      <path d="M314 226H346L363 244H314V226Z" />
      <path d="M138 254H376" />
      <circle cx="164" cy="254" r="23" />
      <circle cx="335" cy="254" r="23" />
      <circle cx="164" cy="254" r="8" />
      <circle cx="335" cy="254" r="8" />
      <path d="M142 212H262" />
      <path d="M142 230H238" />
    </g>
    <g className="driveline-error-illustration-sign">
      <path d="M130 94H226V152H130V94Z" />
      <path d="M150 112H206" />
      <path d="M150 132H188" />
      <path d="M178 152V190" />
    </g>
    <g className="driveline-error-illustration-status">
      <circle cx="370" cy="120" r="44" />
      <path d="M370 94V124" />
      <circle cx="370" cy="144" r="3" />
    </g>
    <g className="driveline-error-illustration-node">
      <circle cx="92" cy="124" r="7" />
      <circle cx="438" cy="186" r="7" />
      <path d="M99 126C166 150 231 144 292 108C342 79 390 83 431 181" />
    </g>
  </svg>
);

export const DrivelineErrorPage = ({
  details,
  eyebrow,
  message,
  primaryAction,
  secondaryActions = [],
  status,
  title,
}: DrivelineErrorPageProps) => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const hasDetails = Boolean(details);

  return (
    <main className="driveline-error-page" aria-labelledby="error-page-title">
      <section className="driveline-error-content">
        <div className="driveline-error-copy" aria-live="polite">
          <div className="driveline-error-heading">
            {eyebrow && <p className="driveline-error-eyebrow">{eyebrow}</p>}
            {status && <span className="driveline-error-status">{status}</span>}
            <h1 id="error-page-title">{title}</h1>
            <div className="driveline-error-message">{message}</div>
          </div>

          <p className="driveline-error-support">
            If this keeps happening, contact{' '}
            <a href={`mailto:${supportEmail}`}>{supportEmail}</a>.
          </p>

          <div className="driveline-error-actions">
            {primaryAction && (
              <ActionButton
                action={{
                  ...primaryAction,
                  variant: primaryAction.variant ?? 'primary',
                }}
              />
            )}
            {secondaryActions.map(action => (
              <ActionButton action={action} key={action.label} />
            ))}
          </div>

          {hasDetails && (
            <div className="driveline-error-details">
              <TegelButtonBase
                aria-controls="error-page-details"
                aria-expanded={detailsOpen}
                className="driveline-error-details-toggle"
                onClick={() => setDetailsOpen(open => !open)}
              >
                {detailsOpen
                  ? 'Hide technical details'
                  : 'Show technical details'}
              </TegelButtonBase>
              {detailsOpen && <pre id="error-page-details">{details}</pre>}
            </div>
          )}
        </div>

        <figure className="driveline-error-visual" aria-hidden="true">
          <DrivelineErrorIllustration />
        </figure>
      </section>
    </main>
  );
};

export const DrivelineNotFoundErrorPage = () => (
  <DrivelineErrorPage
    status="404"
    eyebrow="Page not found"
    title="We could not find that page"
    message="The page may have moved, the link may be incomplete, or the content is not available in Driveline."
    primaryAction={{ label: 'Go to home', onClick: goHome }}
    secondaryActions={[
      { label: 'Go back', onClick: goBack, variant: 'secondary' },
      { label: 'Reload page', onClick: reloadPage, variant: 'ghost' },
    ]}
  />
);

export const DrivelineAppErrorBoundaryFallback = ({
  error,
  plugin,
  resetError,
}: ErrorDisplayProps) => {
  const pluginId = plugin?.pluginId ?? plugin?.id;

  return (
    <DrivelineErrorPage
      status="500"
      eyebrow={pluginId ? `${pluginId} plugin` : 'Application error'}
      title="Something went wrong"
      message={
        pluginId
          ? `The ${pluginId} plugin hit an unexpected error. Try again, or reload Driveline if the issue continues.`
          : 'Driveline hit an unexpected error. Try again, or reload the page if the issue continues.'
      }
      details={errorDetails(error)}
      primaryAction={{ label: 'Try again', onClick: resetError }}
      secondaryActions={[
        { label: 'Reload page', onClick: reloadPage, variant: 'secondary' },
        { label: 'Go to home', onClick: goHome, variant: 'ghost' },
      ]}
    />
  );
};

type RootErrorBoundaryState = {
  error?: Error;
};

export class DrivelineRootErrorBoundary extends Component<
  PropsWithChildren<{}>,
  RootErrorBoundaryState
> {
  static getDerivedStateFromError(error: Error): RootErrorBoundaryState {
    return { error };
  }

  state: RootErrorBoundaryState = {};

  render() {
    if (this.state.error) {
      return (
        <DrivelineErrorPage
          status="500"
          eyebrow="Startup error"
          title="Driveline could not load"
          message="The application failed while loading. Reload the page and contact support if it happens again."
          details={errorDetails(this.state.error)}
          primaryAction={{ label: 'Reload page', onClick: reloadPage }}
          secondaryActions={[
            { label: 'Go to home', onClick: goHome, variant: 'secondary' },
          ]}
        />
      );
    }

    return this.props.children;
  }
}
